import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertOctagon, ExternalLink, Clock, User, Building2 } from "lucide-react";
import api from "@/lib/api";

/**
 * MarginAuthorizations · Admin backlog page.
 *
 * Lists all margin authorization requests (pending first) so the admin
 * has a single-screen backlog to work through. Ligado ao "aviso verde"
 * no topo do Shell — o admin clica no sino e chega aqui.
 *
 * Restringido a admin/direcao pelo backend (GET /margin-authorizations
 * usa require_roles("admin")).
 */

const EUR = (v) => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR",
}).format(Number(v || 0));

const statusStyle = {
  pending:  "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
  expired:  "bg-muted text-muted-foreground border-border",
};

const relativeAge = (iso) => {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const hours = Math.max(0, Math.round((now - then) / 3600000));
    if (hours < 1) return "menos de 1h";
    if (hours < 24) return `há ${hours}h`;
    const days = Math.round(hours / 24);
    return `há ${days} dia${days === 1 ? "" : "s"}`;
  } catch {
    return "—";
  }
};

export default function MarginAuthorizations() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/margin-authorizations${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar pedidos");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, [filter]);   // eslint-disable-line

  if (items === null) {
    return (
      <div className="p-8 lg:p-12 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto space-y-6" data-testid="margin-authz-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2 flex items-center gap-1">
            <AlertOctagon className="w-3 h-3 text-emerald-600" /> Autorizações de margem
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Pedidos para decidir</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Pedidos onde o gestor precisa da tua autorização para avançar
            com uma proposta abaixo da margem-alvo. Clica em cada um para
            abrir o link e aprovar/rejeitar.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {["pending", "approved", "rejected", "expired", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${
                filter === f
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted/40"
              }`}
              data-testid={`margin-authz-filter-${f}`}
            >
              {f === "all" ? "Tudo" : f === "pending" ? "Pendentes" :
                f === "approved" ? "Aprovados" : f === "rejected" ? "Rejeitados" : "Caducados"}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-2xl p-12 text-center bg-card">
          <AlertOctagon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-2xl mb-2">Sem pedidos {filter === "pending" ? "por decidir" : ""}</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {filter === "pending"
              ? "Estás em dia — não há pedidos de autorização por decidir."
              : "Ainda não há pedidos neste estado."}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Lead</th>
                <th className="text-left px-4 py-3">Gestor</th>
                <th className="text-right px-4 py-3">Receita</th>
                <th className="text-right px-4 py-3">Custo AF</th>
                <th className="text-right px-4 py-3">Margem</th>
                <th className="text-left px-4 py-3">Idade</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="margin-authz-tbody">
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-muted/20"
                     data-testid={`margin-authz-row-${it.id.slice(0, 8)}`}>
                  <td className="px-4 py-3">
                    <Link to={`/leads/${it.lead_id}`} className="font-semibold text-foreground hover:underline flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                      {it.lead_name || it.lead_id.slice(0, 8)}
                    </Link>
                    {it.reason && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 max-w-[280px] line-clamp-2 italic">
                        "{it.reason}"
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {it.requested_by_name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{EUR(it.monthly_revenue)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{EUR(it.monthly_af_cost)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-red-600">
                    {it.margin_pct?.toFixed(1)}%
                    <div className="text-[10px] text-muted-foreground font-normal">mín. {it.threshold_pct}%</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {relativeAge(it.requested_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${
                      statusStyle[it.status] || statusStyle.expired
                    }`}>
                      {it.status || "?"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {it.status === "pending" && it.token && (
                      <a
                        href={`/margin-authorization/${it.token}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                        data-testid={`margin-authz-open-${it.id.slice(0, 8)}`}
                      >
                        Rever · <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
